import { parseFilingInterview } from "./filing-status";
import type { InterviewPage } from "./interview-pages";

/** Navigation preferences only. These selections are not answers to tax questions. */
export interface ReturnProfile {
  version: 1;
  selected: string[];
  confirmed: boolean;
}
export type ReturnBuilderProfile = ReturnProfile;

export interface ReturnBuilderStageDefinition {
  id: string;
  label: string;
}
export const returnBuilderStages: readonly ReturnBuilderStageDefinition[] = [
  { id: "about-taxpayer", label: "About Taxpayer" },
  { id: "return-profile", label: "Tell Us About This Return" },
  { id: "documents", label: "Documents" },
  { id: "income", label: "Income" },
  { id: "deductions-credits", label: "Deductions & Credits" },
  { id: "review", label: "Review" },
  { id: "federal-return", label: "Federal Return" },
];

export interface ReturnProfileCard {
  id: string;
  label: string;
  description: string;
  icon: string;
  pageIds: readonly string[];
}

export const returnProfileCards: readonly ReturnProfileCard[] = [
  {
    id: "employee",
    label: "W-2 Employee",
    description: "W-2 wages, tips, and employer benefits",
    icon: "briefcase",
    pageIds: [
      "income-wage-statement-count",
      "income-wage-statement-documents",
      "income-pay-and-tips",
      "income-employer-benefits",
      "income-special-compensation",
      "deductions-additional-opportunities",
      "credits-work-and-retirement",
    ],
  },
  {
    id: "business",
    label: "Business Owner",
    description: "Business and farm records for preparation review",
    icon: "building",
    pageIds: [
      "income-work-and-property",
      "income-estimated-and-extension-payments",
      "deductions-health-and-retirement",
      "credits-foreign-and-other",
    ],
  },
  {
    id: "gig",
    label: "Gig Worker",
    description: "Contract work and self-employment records for review",
    icon: "car",
    pageIds: [
      "income-work-and-property",
      "income-estimated-and-extension-payments",
      "deductions-health-and-retirement",
    ],
  },
  {
    id: "investments",
    label: "Investments",
    description: "Interest, dividends, sales, and digital-asset records",
    icon: "chart",
    pageIds: [
      "income-savings-and-investments",
      "income-sales-debt-and-missing-forms",
      "credits-foreign-and-other",
    ],
  },
  {
    id: "homeowner",
    label: "Homeowner",
    description: "Home ownership, property taxes, and mortgage records",
    icon: "home",
    pageIds: [
      "income-sales-debt-and-missing-forms",
      "deductions-home-and-taxes",
      "deductions-losses-and-other-expenses",
      "credits-home-and-vehicles",
    ],
  },
  {
    id: "dependents",
    label: "Dependents",
    description: "Children, qualifying relatives, and household information",
    icon: "users",
    pageIds: [
      "dependents-household-overview",
      "dependents-home-costs",
      "dependents-qualifying-person",
      "credits-family-and-care",
    ],
  },
  {
    id: "education",
    label: "Education",
    description: "Tuition, student loans, and educator expenses",
    icon: "graduation",
    pageIds: ["deductions-education-and-work", "credits-education"],
  },
  {
    id: "charity",
    label: "Charitable Giving",
    description: "Cash, property donations, and contribution carryovers",
    icon: "heart",
    pageIds: ["deductions-charitable-giving"],
  },
  {
    id: "rental",
    label: "Rental Property",
    description: "Rental and pass-through records for preparation review",
    icon: "house",
    pageIds: ["income-work-and-property"],
  },
  {
    id: "retirement",
    label: "Retirement Income",
    description: "Retirement income, Social Security, and contributions",
    icon: "landmark",
    pageIds: [
      "income-retirement-and-benefits",
      "deductions-health-and-retirement",
      "deductions-additional-opportunities",
      "credits-work-and-retirement",
    ],
  },
];

export interface ReturnBuilderGroup {
  id: string;
  title: string;
  description: string;
  icon: string;
  /** The original pages and bindings, not rewritten source contracts. */
  pages: InterviewPage[];
  profileCardIds: string[];
  preparationSupport: "intake-only" | "review-only";
}
export interface ReturnBuilderStage extends ReturnBuilderStageDefinition {
  groups: ReturnBuilderGroup[];
  pages: InterviewPage[];
}
export interface ReturnBlueprint {
  stages: ReturnBuilderStage[];
  selectedCards: ReturnProfileCard[];
  /** Hidden pages remain unresolved unless separately reviewed using their source answers. */
  unreviewedPages: InterviewPage[];
  /** A navigation profile can never establish tax-screening completeness. */
  screeningComplete: false;
}

const reviewOnlyCards = new Set(["business", "gig", "investments", "rental"]);
const fixedStages = new Set([
  "about-taxpayer",
  "return-profile",
  "documents",
  "review",
  "federal-return",
]);

/** Builds presentation groups only. Does not mutate facts, answers, pages, or profile. */
export function buildReturnBlueprint(
  pages: InterviewPage[],
  profile: ReturnProfile,
  rawFiling: unknown,
): ReturnBlueprint {
  const requested = new Set(
    profile?.version === 1 && Array.isArray(profile.selected)
      ? profile.selected.filter((id): id is string => typeof id === "string")
      : [],
  );
  const selectedCards = returnProfileCards.filter((card) =>
    requested.has(card.id),
  );
  const activeCards =
    profile?.version === 1 && profile.confirmed === true ? selectedCards : [];
  const activePageIds = new Set(
    activeCards.flatMap((card) => [...card.pageIds]),
  );
  const filing = parseFilingInterview(rawFiling);
  const householdNeeded =
    filing.filingStatus === "HOH" || filing.filingStatus === "QSS";
  const spouseRelevant =
    filing.filingStatus === "MFJ" ||
    filing.filingStatus === "MFS" ||
    (filing.filingStatus === "HOH" &&
      (filing.maritalSituation === "MARRIED" ||
        (filing.maritalSituation === "WIDOWED" &&
          filing.spouseDeathYear === "2025")));
  const stages = returnBuilderStages.map((stage) => ({
    ...stage,
    groups: [] as ReturnBuilderGroup[],
    pages: [] as InterviewPage[],
  }));
  const used = new Set<InterviewPage>();
  const find = (ids: readonly string[]) =>
    ids.flatMap((id) =>
      pages.filter((page) => page.id === id && !used.has(page)),
    );
  const add = (
    stageId: string,
    id: string,
    title: string,
    description: string,
    icon: string,
    candidates: InterviewPage[],
    forceReviewOnly = false,
  ) => {
    const included = candidates.filter((page) => !used.has(page));
    if (!included.length) return;
    included.forEach((page) => used.add(page));
    const profileCardIds = activeCards
      .filter((card) => included.some((page) => card.pageIds.includes(page.id)))
      .map((card) => card.id);
    const stage = stages.find((entry) => entry.id === stageId)!;
    stage.groups.push({
      id,
      title,
      description,
      icon,
      pages: included,
      profileCardIds,
      preparationSupport:
        forceReviewOnly ||
        profileCardIds.some((card) => reviewOnlyCards.has(card))
          ? "review-only"
          : "intake-only",
    });
    stage.pages.push(...included);
  };
  add(
    "about-taxpayer",
    "personal-information",
    "Personal information",
    "Taxpayer details, contact information, and residence.",
    "users",
    find([
      "filing-status-tax-year",
      "filing-status-taxpayer-name",
      "filing-status-taxpayer-birth",
      "filing-status-taxpayer-occupation",
      "filing-status-taxpayer-contact",
      "filing-status-taxpayer-residence",
    ]),
  );
  add(
    "about-taxpayer",
    "mailing-address",
    "Mailing address",
    "The address used for this federal return.",
    "home",
    find(["filing-status-taxpayer-address"]),
  );
  add(
    "about-taxpayer",
    "filing-status",
    "Filing status",
    "The intended filing status and relevant family circumstances.",
    "users",
    find(["filing-status-choice", "filing-status-relationship"]),
  );
  if (spouseRelevant)
    add(
      "about-taxpayer",
      "spouse-information",
      "Spouse information",
      "Spouse details relevant to this filing status.",
      "users",
      pages.filter((page) => page.stageId === "spouse-information"),
    );
  if (householdNeeded || activeCards.some((card) => card.id === "dependents"))
    add(
      "about-taxpayer",
      "dependents",
      "Dependents & household",
      "Dependent and household information needed for preparation review.",
      "users",
      pages.filter((page) => page.stageId === "dependents"),
    );

  add(
    "documents",
    "tax-documents",
    "Tax documents",
    "Organize the records received and identify missing documents.",
    "briefcase",
    find([
      ...(activeCards.some((card) => card.id === "employee")
        ? ["income-wage-statement-count", "income-wage-statement-documents"]
        : []),
      "review-supporting-documents",
    ]),
  );

  const incomeGroups: [string, string, string, string, string[]][] = [
    [
      "employment-income",
      "Wages & employment",
      "Wages, tips, and employer-provided benefits.",
      "briefcase",
      [
        "income-pay-and-tips",
        "income-employer-benefits",
        "income-special-compensation",
      ],
    ],
    [
      "business-property-income",
      "Business & property income",
      "Records for review; business, farm, and rental schedule preparation is not available in this release.",
      "building",
      ["income-work-and-property"],
    ],
    [
      "investment-income",
      "Savings & investments",
      "Investment records for review; investment-return preparation is not available in this release.",
      "chart",
      ["income-savings-and-investments"],
    ],
    [
      "retirement-income",
      "Retirement income",
      "Pensions, distributions, Social Security, and Railroad Retirement information.",
      "landmark",
      ["income-retirement-and-benefits"],
    ],
    [
      "sales-and-other-income",
      "Sales & other income",
      "Home sales, debt cancellation, and records that may need additional review.",
      "home",
      ["income-sales-debt-and-missing-forms"],
    ],
    [
      "tax-payments",
      "Tax payments",
      "Estimated and extension payments made outside employer withholding.",
      "landmark",
      ["income-estimated-and-extension-payments"],
    ],
  ];
  for (const [id, title, description, icon, ids] of incomeGroups)
    add(
      "income",
      id,
      title,
      description,
      icon,
      find(ids.filter((pageId) => activePageIds.has(pageId))),
      id === "business-property-income" || id === "investment-income",
    );

  for (const card of activeCards) {
    const included = find(card.pageIds).filter(
      (page) => page.stageId === "deductions" || page.stageId === "credits",
    );
    add(
      "deductions-credits",
      `${card.id}-deductions-credits`,
      card.label,
      "Potential deductions and credits to review with the taxpayer’s records. Selection does not establish eligibility.",
      card.icon,
      included,
      reviewOnlyCards.has(card.id),
    );
  }

  add(
    "review",
    "preparation-review",
    "Preparation review",
    "Review the recorded information, unresolved topics, and preparation limitations. Hidden topics are not answered by the profile.",
    "briefcase",
    pages.filter(
      (page) =>
        page.stageId === "review" ||
        page.id === "filing-status-identity-review",
    ),
    true,
  );
  add(
    "federal-return",
    "federal-return-package",
    "Federal return",
    "Return preferences and preparation availability. This workflow does not certify or file a return.",
    "landmark",
    pages.filter((page) => page.stageId === "federal-return"),
    true,
  );

  return {
    stages: stages.filter(
      (stage) => fixedStages.has(stage.id) || stage.pages.length > 0,
    ),
    selectedCards,
    unreviewedPages: pages.filter((page) => !used.has(page)),
    screeningComplete: false,
  };
}
